/* Settings, Seite „Tastenkürzel“: Liste nach Gruppen mit Suche, Kürzel per Tastendruck zuweisen, zurücksetzen. */
import { useEffect, useReducer, useState } from 'react';
import { createPortal } from 'react-dom';
import Keys from '../keys.ts';
import { useT } from './hooks.ts';
import { DialogBody } from './dialog.tsx';

interface Listening { id: string; slot: number }

export function KeysTab(props: { foot: HTMLElement | null }) {
  const t = useT();
  const mac = Keys.isMac();
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const [filter, setFilter] = useState('');
  const [msg, setMsg] = useState<string>(t('keys.intro'));
  const [listening, setListening] = useState<Listening | null>(null);

  // Während des Zuweisens bekommt dieses Fenster alle Tastendrücke
  useEffect(() => {
    if (!listening) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault(); e.stopImmediatePropagation();
      if (e.key === 'Escape') { setListening(null); setMsg(t('keys.cancelled')); return; }
      const c = Keys.fromEvent(e);
      if (!c) return;   // nur Umschalt/Strg/Alt gedrückt
      const stolen = Keys.assign(listening.id, listening.slot, c), a = Keys.byId(listening.id);
      setListening(null);
      setMsg(t('keys.assigned', { combo: Keys.fmt(c), name: a ? a.n : listening.id })
        + (stolen.length ? ' ' + t('keys.stolen', { names: stolen.map((s) => t('keys.quoted', { name: Keys.byId(s)?.n || s })).join(', ') }) : '')
        + (Keys.reserved(c) ? ' ' + t('keys.reserved') : ''));
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [listening, t]);

  const f = filter.trim().toLowerCase();
  const is = (id: string, slot: number) => !!listening && listening.id === id && listening.slot === slot;
  const groups = Keys.groups.map((g) => ({ g, acts: Keys.list.filter((a) => a.g === g && (!f || a.n.toLowerCase().indexOf(f) >= 0 || Keys.fmtAll(a.id, mac).toLowerCase().indexOf(f) >= 0)) })).filter((x) => x.acts.length);
  const fixed = Keys.fixed.filter((x) => !f || (x[1] + ' ' + x[2] + ' ' + x[0]).toLowerCase().indexOf(f) >= 0);

  return (
    <DialogBody>
      <div className="row">
        <input type="text" className="inp" placeholder={t('keys.search')} aria-label={t('keys.search_aria')} autoComplete="off" spellCheck={false} value={filter} onChange={(e) => setFilter(e.target.value)} />
        <span className="small">{mac ? t('keys.mac_legend') : t('keys.mac_note')}</span>
      </div>
      <div className="keys-list">
        {groups.map(({ g, acts }) => (
          <div key={g} style={{ display: 'contents' }}>
            <h4>{Keys.groupName(g)}</h4>
            {acts.map((a) => (
              <div key={a.id} className={'krow' + (Keys.isCustom(a.id) ? ' custom' : '')}>
                <span className="kname">{a.n}</span>
                <span className="kcombos">
                  {Keys.get(a.id).map((c, i) => (
                    <span key={i} className={'kchip' + (is(a.id, i) ? ' on' : '')}>
                      <button className="kbtn" title={t('keys.click_tip')} onClick={() => setListening({ id: a.id, slot: i })}>{is(a.id, i) ? t('keys.press') : Keys.fmt(c)}</button>
                      <button className="kx" title={t('keys.remove')} aria-label={t('keys.remove_aria', { combo: Keys.fmt(c) })} onClick={() => { setListening(null); Keys.removeSlot(a.id, i); bump(); }}>×</button>
                    </span>
                  ))}
                  <button className={'kadd' + (is(a.id, -1) ? ' on' : '')} title={t('keys.add')} aria-label={t('keys.add_aria', { name: a.n })} onClick={() => setListening({ id: a.id, slot: -1 })}>{is(a.id, -1) ? t('keys.press') : '+'}</button>
                </span>
                <button className="btn sm kreset" title={t('keys.reset')} aria-label={t('keys.reset_aria', { name: a.n })} hidden={!Keys.isCustom(a.id)} onClick={() => { setListening(null); Keys.reset(a.id); bump(); }}>↺</button>
              </div>
            ))}
          </div>
        ))}
        {fixed.length > 0 && (
          <>
            <h4>{t('keys.fixed')}</h4>
            {fixed.map((x, i) => (
              <div key={i} className="krow fixed"><span className="kname">{x[2]}</span><span className="kcombos"><span className="kchip static"><span className="kbtn">{x[1]}</span></span></span></div>
            ))}
          </>
        )}
        {!groups.length && !fixed.length && <p className="small">{t('keys.none')}</p>}
      </div>
      <p className="small kmsg" role="status">{msg}</p>
      {props.foot && createPortal(
        <button className="btn" title={t('keys.reset_all_tip')} onClick={() => { setListening(null); Keys.resetAll(); setMsg(t('keys.reset_all_done')); bump(); }}>{t('keys.reset_all')}</button>, props.foot)}
    </DialogBody>
  );
}
